<template>
  <v-navigation-drawer
    :value="visible"
    @input="$emit('update:visible', $event)"
    right
    temporary
    fixed
    width="500"
    class="history-drawer"
  >
    <!-- Header -->
    <div class="hd-header">
      <div class="hd-header-left">
        <div class="hd-header-icon">
          <v-icon color="white" size="18">mdi-history</v-icon>
        </div>
        <div>
          <h3 class="hd-title">Historial de Cambios</h3>
          <span class="hd-subtitle">Cambios agrupados por semana</span>
        </div>
      </div>
      <v-btn icon small @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-divider />

    <!-- Content -->
    <div class="hd-content">
      <div v-if="groupedHistory.length === 0" class="hd-empty">
        <v-icon size="40" color="#cbd5e1">mdi-clipboard-text-clock-outline</v-icon>
        <span class="hd-empty-text">No se registran modificaciones</span>
        <span class="hd-empty-hint">Los ajustes realizados aparecerán aquí</span>
      </div>

      <div v-else class="hd-week-list">
        <div
          v-for="weekGroup in groupedHistory"
          :key="weekGroup.pdsID"
          class="hd-week-group"
        >
          <!-- Week Header -->
          <div class="hd-week-header">
            <v-icon small color="#3b5bdb" class="mr-2">mdi-calendar-week</v-icon>
            <span class="hd-week-name">{{ weekGroup.weekName }}</span>
            <v-chip
              v-if="weekGroup.changeCount >= 3"
              x-small
              color="warning"
              outlined
              class="ml-2"
            >
              <v-icon x-small class="mr-1">mdi-alert-outline</v-icon>
              {{ weekGroup.changeCount }} cambios
            </v-chip>
          </div>

          <!-- Timeline of changes within this week -->
          <div class="hd-timeline">
            <div
              v-for="(event, idx) in weekGroup.events"
              :key="idx"
              class="hd-event"
            >
              <!-- Timeline dot and connector -->
              <div class="hd-event-dot-col">
                <div
                  class="hd-event-dot"
                  :class="{
                    'hd-dot--create': event.type === 'create',
                    'hd-dot--edit': event.type === 'edit',
                    'hd-dot--delete': event.type === 'delete',
                  }"
                >
                  <v-icon x-small color="white">
                    {{ event.type === 'create' ? 'mdi-plus' : event.type === 'delete' ? 'mdi-minus' : 'mdi-pencil' }}
                  </v-icon>
                </div>
                <div v-if="idx < weekGroup.events.length - 1" class="hd-event-line"></div>
              </div>

              <!-- Event content -->
              <div class="hd-event-content">
                <div class="hd-event-meta">
                  <span class="hd-event-date">{{ formatDateTime(event.date) }}</span>
                  <v-chip
                    x-small
                    :color="event.type === 'create' ? 'success' : event.type === 'delete' ? 'error' : 'primary'"
                    outlined
                    class="ml-1"
                  >
                    {{ event.type === 'create' ? 'Nuevo' : event.type === 'delete' ? 'Eliminado' : 'Ajuste' }}
                  </v-chip>
                </div>
                <div class="hd-event-user">
                  <v-icon x-small color="#94a3b8" class="mr-1">mdi-account-outline</v-icon>
                  {{ event.userName }}
                </div>

                <!-- Turn-level changes within this event -->
                <div class="hd-event-turns">
                  <div
                    v-for="(change, ci) in event.changes"
                    :key="ci"
                    class="hd-change-row"
                  >
                    <span class="hd-change-turn">
                      <v-icon x-small class="mr-1" :color="change.typeTurn == 1 ? '#f59e0b' : change.typeTurn == 2 ? '#6366f1' : '#10b981'">
                        {{ change.typeTurn == 1 ? 'mdi-weather-sunny' : change.typeTurn == 2 ? 'mdi-weather-night' : 'mdi-swap-horizontal' }}
                      </v-icon>
                      {{ change.turnName }}:
                    </span>
                    <span class="hd-change-before">{{ change.qtyBefore }}</span>
                    <v-icon x-small color="#94a3b8" class="mx-1">mdi-arrow-right</v-icon>
                    <span class="hd-change-after">{{ change.qtyAfter }}</span>
                    <span class="hd-change-split">
                      (H:{{ change.maleBefore }}→{{ change.maleAfter }} / M:{{ change.femaleBefore }}→{{ change.femaleAfter }})
                    </span>
                  </div>
                </div>

                <!-- Event total delta -->
                <div v-if="event.changes.length > 1" class="hd-event-total">
                  Total:
                  <span class="hd-change-before">{{ event.totalBefore }}</span>
                  <v-icon x-small color="#94a3b8" class="mx-1">mdi-arrow-right</v-icon>
                  <span class="hd-change-after font-weight-bold">{{ event.totalAfter }}</span>
                  <v-chip
                    x-small
                    :color="event.totalDelta > 0 ? 'success' : event.totalDelta < 0 ? 'error' : 'default'"
                    outlined
                    class="ml-1"
                  >
                    {{ event.totalDelta > 0 ? '+' : '' }}{{ event.totalDelta }}
                  </v-chip>
                </div>

                <!-- Significant change alert -->
                <div
                  v-if="isSignificantChange(event)"
                  class="hd-significant-badge"
                >
                  <v-icon x-small color="#d97706" class="mr-1">mdi-lightning-bolt</v-icon>
                  Cambio significativo
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="hd-footer">
      <v-btn outlined color="grey darken-1" small block class="hd-close-btn" @click="$emit('close')">
        Cerrar
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "HistoryDrawer",
  props: {
    visible: { type: Boolean, default: false },
    historyList: { type: Array, default: () => [] },
    reqQuantity: { type: Number, default: 0 },
  },
  computed: {
    groupedHistory() {
      if (!this.historyList || this.historyList.length === 0) return [];

      // Step 1: Group by PdsName (week)
      const weekMap = {};
      this.historyList.forEach(log => {
        const weekKey = log.PdsName || "Sin semana";
        if (!weekMap[weekKey]) {
          weekMap[weekKey] = {
            weekName: weekKey,
            pdsID: log.PdsID,
            logs: [],
          };
        }
        weekMap[weekKey].logs.push(log);
      });

      // Step 2: Within each week, group by timestamp (same SecCreate = same event)
      const result = Object.values(weekMap).map(week => {
        const eventMap = {};
        week.logs.forEach(log => {
          // Group by SecCreate + UsrCreateID (same user, same time = same event)
          const timeKey = this.normalizeTimestamp(log.SecCreate) + "_" + log.UsrCreateID;
          if (!eventMap[timeKey]) {
            eventMap[timeKey] = {
              date: log.SecCreate,
              userName: log.UsrCreateName || log.UsrName || "Sistema",
              changes: [],
              type: "edit",
            };
          }
          const change = {
            typeTurn: log.TypeTurn,
            turnName: log.TypeTurnName || this.getTurnName(log.TypeTurn),
            qtyBefore: log.WphQuantityBefore,
            qtyAfter: log.WphQuantityAfter,
            maleBefore: log.WphQuantityMaleBefore,
            maleAfter: log.WphQuantityMaleAfter,
            femaleBefore: log.WphQuantityFemaleBefore,
            femaleAfter: log.WphQuantityFemaleAfter,
          };
          eventMap[timeKey].changes.push(change);

          // Determine event type
          if (log.WphQuantityBefore === 0 && log.WphQuantityAfter > 0) {
            if (eventMap[timeKey].type !== "edit") eventMap[timeKey].type = "create";
            if (eventMap[timeKey].changes.length === 1) eventMap[timeKey].type = "create";
          }
          if (log.WphQuantityAfter === 0 && log.WphQuantityBefore > 0) {
            eventMap[timeKey].type = "delete";
          }
        });

        // Compute totals for each event
        const events = Object.values(eventMap)
          .map(ev => {
            ev.totalBefore = ev.changes.reduce((sum, c) => sum + (c.qtyBefore || 0), 0);
            ev.totalAfter = ev.changes.reduce((sum, c) => sum + (c.qtyAfter || 0), 0);
            ev.totalDelta = ev.totalAfter - ev.totalBefore;
            // Refine type
            if (ev.totalBefore === 0 && ev.totalAfter > 0) ev.type = "create";
            else if (ev.totalAfter === 0 && ev.totalBefore > 0) ev.type = "delete";
            else ev.type = "edit";
            return ev;
          })
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        return {
          weekName: week.weekName,
          pdsID: week.pdsID,
          events: events,
          changeCount: events.length,
        };
      });

      return result.sort((a, b) => (a.pdsID || 0) - (b.pdsID || 0));
    },
  },
  methods: {
    getTurnName(typeTurn) {
      const t = Number(typeTurn);
      if (t === 1) return "Día";
      if (t === 2) return "Noche";
      return "Mixto";
    },
    normalizeTimestamp(dt) {
      if (!dt) return "";
      // Round to the nearest second to group events fired together
      const d = new Date(dt);
      return d.toISOString().slice(0, 19);
    },
    formatDateTime(dt) {
      if (!dt) return "";
      const d = new Date(dt);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const mins = String(d.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${mins}`;
    },
    isSignificantChange(event) {
      if (!this.reqQuantity || this.reqQuantity === 0) return false;
      const delta = Math.abs(event.totalDelta || 0);
      return delta > this.reqQuantity * 0.3;
    },
  },
};
</script>

<style scoped>
/* ── Drawer ──────────────────────────── */
.history-drawer {
  display: flex;
  flex-direction: column;
  z-index: 100 !important;
}

/* ── Header ──────────────────────────── */
.hd-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  flex-shrink: 0;
}

.hd-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hd-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.hd-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.hd-subtitle {
  font-size: 11px;
  color: #94a3b8;
}

/* ── Content ─────────────────────────── */
.hd-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

/* ── Empty ────────────────────────────── */
.hd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 24px;
  text-align: center;
}

.hd-empty-text {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}

.hd-empty-hint {
  font-size: 12px;
  color: #94a3b8;
}

/* ── Week Groups ─────────────────────── */
.hd-week-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.hd-week-group {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #3b5bdb;
  border-radius: 10px;
  padding: 16px;
  transition: box-shadow 0.2s;
}

.hd-week-group:hover {
  box-shadow: 0 4px 16px rgba(59, 91, 219, 0.08);
}

.hd-week-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}

.hd-week-name {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

/* ── Timeline ────────────────────────── */
.hd-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.hd-event {
  display: flex;
  gap: 12px;
}

.hd-event-dot-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 24px;
}

.hd-event-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hd-dot--create {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
}

.hd-dot--edit {
  background: linear-gradient(135deg, #3b5bdb 0%, #5b7cf7 100%);
}

.hd-dot--delete {
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
}

.hd-event-line {
  width: 2px;
  flex: 1;
  min-height: 12px;
  background: #e2e8f0;
}

.hd-event-content {
  flex: 1;
  padding-bottom: 16px;
}

.hd-event-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.hd-event-date {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.hd-event-user {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 8px;
}

/* ── Change Rows ─────────────────────── */
.hd-event-turns {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #f8fafc;
  border-radius: 6px;
  padding: 8px 10px;
}

.hd-change-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.hd-change-turn {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #475569;
  min-width: 70px;
}

.hd-change-before {
  color: #94a3b8;
  text-decoration: line-through;
  font-weight: 500;
}

.hd-change-after {
  color: #1e293b;
  font-weight: 600;
}

.hd-change-split {
  font-size: 10px;
  color: #94a3b8;
  margin-left: 4px;
}

.hd-event-total {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e2e8f0;
}

/* ── Significant Badge ───────────────── */
.hd-significant-badge {
  display: inline-flex;
  align-items: center;
  margin-top: 6px;
  padding: 3px 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #d97706;
}

/* ── Footer ──────────────────────────── */
.hd-footer {
  flex-shrink: 0;
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
}

.hd-close-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}
</style>
